/**
 * Platform Context
 *
 * Provides platform-specific hooks and components to shared UI.
 * Each app (Excel, Web, PowerPoint) provides its own platform configuration.
 */

import { createContext, useContext, type ComponentType, type ReactNode } from "react";

export interface ShortcutEntry {
  keys: string;
  label: string;
}

export interface PlatformConfig {
  /** Keyboard shortcut display strings */
  shortcuts: {
    focusChat: string;
    newAgent: string;
  };

  /** Full list of shortcuts for settings dialog */
  shortcutsList?: ShortcutEntry[];

  /** Register platform selection tracking (called as side effect in prompt input) */
  useSelection: () => void;

  /** Navigate to a range in the platform's document */
  navigateToRange: (sheet: string, range: string) => void;

  /**
   * Custom component for rendering range references in markdown.
   * If not provided, range references render as plain <code>.
   */
  RangeBadge?: ComponentType<{ worksheet: string; range: string; label?: string }>;
}

const defaultConfig: PlatformConfig = {
  shortcuts: { focusChat: "", newAgent: "" },
  useSelection: () => {},
  navigateToRange: () => {},
};

const PlatformContext = createContext<PlatformConfig>(defaultConfig);

export function PlatformProvider({
  value,
  children,
}: {
  value: PlatformConfig;
  children: ReactNode;
}) {
  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform(): PlatformConfig {
  return useContext(PlatformContext);
}
